import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Check if already logged in
    const token = localStorage.getItem('admin_token');
    if (token) {
      navigate('/admin/dashboard');
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const endpoint = isRegister ? '/api/auth/register' : '/api/auth/login';
      const body = isRegister 
        ? { email, password, name }
        : { email, password };

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Error de autenticación');
      }

      if (isRegister) {
        // After registration, login automatically
        setIsRegister(false);
        setError('');
        alert('Registro exitoso. Por favor inicia sesión.');
      } else {
        localStorage.setItem('admin_token', data.access_token);
        navigate('/admin/dashboard');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_faro-player-showcase/artifacts/6o32bteg_Logo_Blanco.png"
            alt="FARO Sports"
            className="h-12 mx-auto mb-6"
          />
          <h1 className="text-2xl font-light text-white">
            {isRegister ? 'Crear Cuenta Admin' : 'Panel de Administración'}
          </h1>
          <p className="text-white/40 text-sm mt-2">
            {isRegister ? 'Registra una nueva cuenta' : 'Ingresa tus credenciales para continuar'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white/5 rounded-2xl p-8 border border-white/10">
          {error && (
            <div className="flex items-center gap-3 p-4 mb-6 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400">
              <AlertCircle size={20} />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {isRegister && (
            <div className="mb-5">
              <label className="block text-white/80 text-sm font-light mb-2">Nombre</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required={isRegister}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/20 focus:border-white/30 focus:ring-1 focus:ring-white/20 transition-all duration-300 outline-none font-light"
                placeholder="Tu nombre"
              />
            </div>
          )}

          <div className="mb-5">
            <label className="block text-white/80 text-sm font-light mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/20 focus:border-white/30 focus:ring-1 focus:ring-white/20 transition-all duration-300 outline-none font-light"
              placeholder="admin@farosports.com"
              data-testid="admin-email-input"
            />
          </div>

          <div className="mb-6">
            <label className="block text-white/80 text-sm font-light mb-2">Contraseña</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/20 focus:border-white/30 focus:ring-1 focus:ring-white/20 transition-all duration-300 outline-none font-light pr-12"
                placeholder="••••••••"
                data-testid="admin-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full px-6 py-4 bg-white hover:bg-white/90 text-black font-medium rounded-xl transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-50"
            data-testid="admin-login-btn"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
            ) : (
              isRegister ? 'Crear Cuenta' : 'Iniciar Sesión'
            )}
          </button>

          <button
            type="button"
            onClick={() => {
              setIsRegister(!isRegister);
              setError('');
            }}
            className="w-full mt-4 text-white/50 hover:text-white text-sm font-light transition-colors"
          >
            {isRegister ? '¿Ya tienes cuenta? Inicia sesión' : '¿Primera vez? Crear cuenta'}
          </button>
        </form>

        {/* Back to site */}
        <div className="text-center mt-6">
          <a href="/" className="text-white/40 hover:text-white text-sm transition-colors">
            ← Volver al sitio
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
